/**
 * Logging System
 *
 * Clean export interface for the entire logging subsystem.
 * This is the only file that should be imported by application code.
 *
 * @example
 * ```typescript
 * import { logger } from '~/logging';
 *
 * // Level-based logging
 * logger.L3.info('Processing candidate');
 *
 * // Domain logging
 * logger.domain.compliance('Checking five-year rule');
 *
 * // Phase tracking
 * logger.phase.enter('optimization', { candidateId: 'abc' });
 * // ... do work ...
 * logger.phase.exit('optimization', { success: true });
 *
 * // Context accumulation
 * logger.withContext({ userId: '123' });
 * logger.L2.info('User action'); // Will include userId in context
 *
 * // Debug sessions
 * logger.debug.start('feature-debug');
 * console.log('This will be tracked');
 * logger.debug.end('feature-debug');
 * ```
 */

import { LoggerImpl } from './logger';

// Create singleton logger instance
const loggerInstance = new LoggerImpl();

// Export the logger instance
export const logger = loggerInstance;

// Export configuration utilities
export { getConfigSummary, getLogConfig } from './config';
// Re-export types for consumer convenience
export type {
  DebugSessionManager,
  DomainLogger,
  LevelLogger,
  LogConfig,
  LogContext,
  LogEntry,
  LogLevel,
  LogPreset,
  PhaseManager,
} from './types';

// Default export for convenience
export default logger;
